// Coverage Years Management Page (admin only)
import { useState, useEffect, useCallback } from 'react';
import portalApi from '../lib/api';
import { Calendar, Plus, Loader2, Pencil, Archive, Star, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { useAuth } from '../hooks/useAuth';
import { IntakeTypeSettings } from '../components/IntakeTypeSettings';

export function CoverageYearsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ year: new Date().getFullYear() + 1, label: '', is_active: true, is_default: false });

  const [renewOpen, setRenewOpen] = useState(false);
  const [renewSource, setRenewSource] = useState(null);
  const [renewTarget, setRenewTarget] = useState('');
  const [renewWorkspace, setRenewWorkspace] = useState('');
  const [renewPremium, setRenewPremium] = useState('');
  const [workspaces, setWorkspaces] = useState([]);
  const [cleanupYear, setCleanupYear] = useState(null);
  const [cleaning, setCleaning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setYears(await portalApi.getCoverageYears());
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Coverage Years · Latinos Salud';
    load();
    portalApi.getAllWorkspaces().then(setWorkspaces).catch(() => portalApi.getWorkspaces().then(setWorkspaces).catch(() => {}));
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ year: new Date().getFullYear() + 1, label: '', is_active: true, is_default: false });
    setDialogOpen(true);
  };

  const openEdit = (y) => {
    setEditingId(y.id);
    setForm({ year: y.year, label: y.label, is_active: y.is_active, is_default: y.is_default });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingId) {
        await portalApi.updateCoverageYear(editingId, { label: form.label, is_active: form.is_active, is_default: form.is_default });
        toast.success('Coverage year updated');
      } else {
        await portalApi.createCoverageYear({ year: parseInt(form.year, 10), label: form.label || undefined, is_active: form.is_active, is_default: form.is_default });
        toast.success('Coverage year created');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (y) => {
    try {
      await portalApi.updateCoverageYear(y.id, { is_archived: !y.is_archived });
      toast.success(y.is_archived ? 'Year unarchived' : 'Year archived');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const openRenew = (y) => {
    setRenewSource(y);
    setRenewTarget('');
    setRenewWorkspace('');
    setRenewPremium('Active');
    setRenewOpen(true);
  };

  const handleRenew = async () => {
    if (!renewTarget) { toast.error('Select a target year'); return; }
    setSaving(true);
    try {
      const res = await portalApi.createRenewals(renewSource.id, renewTarget, {
        workspace_key: renewWorkspace || undefined,
        premium_support_status: renewPremium || undefined,
      });
      toast.success(`Created ${res.created} renewals (${res.skipped} skipped)`);
      setRenewOpen(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCleanup = async () => {
    if (!cleanupYear) return;
    setCleaning(true);
    try {
      const res = await portalApi.cleanupNonActiveRenewals(cleanupYear.id);
      toast.success(`Removed ${res.deleted} non-active renewal${res.deleted === 1 ? '' : 's'}`);
      setCleanupYear(null);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="coverage-years-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Coverage Years</h1>
          <p className="text-slate-500 mt-1">{years.length} years configured</p>
        </div>
        <Button onClick={openCreate} data-testid="new-year-btn"><Plus className="h-4 w-4 mr-2" />New Year</Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-sky-500" /></div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {years.map(y => (
            <div key={y.id} className="bg-white rounded-xl p-6 border border-slate-200" data-testid={`year-card-${y.year}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-sky-500" />
                  <h3 className="text-lg font-semibold text-slate-900">{y.label}</h3>
                </div>
                {y.is_default && <Badge className="bg-amber-100 text-amber-700"><Star className="h-3 w-3 mr-1" />Default</Badge>}
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {y.is_active ? <Badge className="bg-green-100 text-green-700">Active</Badge> : <Badge className="bg-slate-100 text-slate-700">Inactive</Badge>}
                {y.is_archived && <Badge className="bg-slate-100 text-slate-700">Archived</Badge>}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => openEdit(y)} data-testid={`edit-year-${y.year}`}><Pencil className="h-4 w-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => openRenew(y)} data-testid={`renew-year-${y.year}`}><RefreshCw className="h-4 w-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => handleArchive(y)} data-testid={`archive-year-${y.year}`}><Archive className="h-4 w-4" /></Button>
                {isAdmin && (
                  <Button variant="outline" size="sm" className="text-rose-500 hover:text-rose-700 hover:bg-rose-50" onClick={() => setCleanupYear(y)} data-testid={`cleanup-year-${y.year}`} title="Remove non-active renewals"><Trash2 className="h-4 w-4" /></Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <IntakeTypeSettings />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="year-dialog">
          <DialogHeader><DialogTitle>{editingId ? 'Edit Coverage Year' : 'New Coverage Year'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Year *</Label><Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} disabled={Boolean(editingId)} data-testid="year-input" /></div>
            <div className="space-y-2"><Label>Label</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder={`${form.year} Coverage Year`} /></div>
            <div className="flex items-center gap-2"><Checkbox id="is_active" checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} /><Label htmlFor="is_active" className="cursor-pointer">Active</Label></div>
            <div className="flex items-center gap-2"><Checkbox id="is_default" checked={form.is_default} onCheckedChange={(v) => setForm({ ...form, is_default: v })} /><Label htmlFor="is_default" className="cursor-pointer">Set as Default</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-year-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={renewOpen} onOpenChange={setRenewOpen}>
        <DialogContent data-testid="renew-dialog">
          <DialogHeader><DialogTitle>Bulk Renewals from {renewSource?.label}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">This will create renewal cases for all cases in {renewSource?.label} into the selected target year.</p>
            <div className="space-y-2"><Label>Target Year *</Label>
              <Select value={renewTarget} onValueChange={setRenewTarget}>
                <SelectTrigger data-testid="renew-target-select"><SelectValue placeholder="Select target year" /></SelectTrigger>
                <SelectContent>{years.filter(y => y.id !== renewSource?.id && !y.is_archived).map(y => <SelectItem key={y.id} value={y.id}>{y.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Workspace</Label>
              <Select value={renewWorkspace || 'all'} onValueChange={(v) => setRenewWorkspace(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="renew-workspace-select"><SelectValue placeholder="All workspaces" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All workspaces</SelectItem>
                  {workspaces.map((w) => <SelectItem key={w.id || w.key} value={w.key || w.workspace_key}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Premium Support status</Label>
              <Select value={renewPremium || 'any'} onValueChange={(v) => setRenewPremium(v === 'any' ? '' : v)}>
                <SelectTrigger data-testid="renew-premium-select"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  {['Active', 'Pending', 'Not Eligible', 'Suspended', 'Closed'].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-slate-400">Only cases matching these filters in {renewSource?.label} will be renewed into the target year as "Pending Renewal Review". By default only <strong>Active</strong> Premium Support clients are renewed; the consent status is carried over (a consent stays valid while the client remains your client without interruption).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenewOpen(false)}>Cancel</Button>
            <Button onClick={handleRenew} disabled={saving} data-testid="confirm-renew-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Renewals'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!cleanupYear} onOpenChange={(o) => !o && setCleanupYear(null)}>
        <AlertDialogContent data-testid="cleanup-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove non-active renewals from {cleanupYear?.label}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes every renewal case in {cleanupYear?.label} whose Premium Support
              status is NOT "Active" (suspended, not eligible, closed or pending). Active clients are kept.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-cleanup-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); handleCleanup(); }} disabled={cleaning} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-cleanup-btn">
              {cleaning ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Remove non-active'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default CoverageYearsPage;
